import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useUpdateConsumableReqMutation, useUpdateBoPartialConsumableMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useUpdateconsumfinalMutation } from '../../../../features/consumfinal/consumfinalApiSlice';
import { useUpdateOfficeItemMutation } from '../../../../features/officeitems/officeItemsApiSlice';
import { useAddNewConsumTransactionMutation } from '../../../../features/consumtransactions/consumTransApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import UnivConsumSelector from '../UnivConsumSelector';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import { useAlert } from '../../../ui/AlertProvider';

// Styled Components
const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const AcceptButton = styled(StyledButton)`
  background-color: #4caf50;
  
  &:hover {
    background-color: #45a049;
  }
`;

const RejectButton = styled(StyledButton)`
  background-color: #f44336;
  
  &:hover {
    background-color: #da190b;
  }
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const ConsumablesContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const ConsumablesTitle = styled.h3`
  margin-bottom: 10px;
  color: #1976d2;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const NotificationMessage = styled.div`
  margin: 15px 0;
  padding: 12px 16px;
  border-radius: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  background-color: ${(props) => {
    if (props.type === 'partial') return '#fff3cd';
    if (props.type === 'full') return '#d1ecf1';
    if (props.type === 'store') return '#d4edda';
    return '#f8f9fa';
  }};
  border-left: 4px solid ${(props) => {
    if (props.type === 'partial') return '#ffc107';
    if (props.type === 'full') return '#17a2b8';
    if (props.type === 'store') return '#28a745';
    return '#6c757d';
  }};
  color: #333;
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 5px 10px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
`;

const SummaryItemName = styled.span`
  font-weight: 500;
  margin-right: 5px;
`;

const SummaryItemCount = styled.span`
  background-color: #007bff;
  color: white;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.8rem;
  min-width: 20px;
  text-align: center;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const SelectedAssetsTitle = styled.h3`
  margin: 0;
  color: #1976d2;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ReSubmitButton = styled(StyledButton)`
  background-color: #28a745;
  margin-top: 20px;
  
  &:hover {
    background-color: #218838;
  }
  
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(StyledButton)`
  background-color: #6c757d;
  
  &:hover {
    background-color: #5a6268;
  }
`;

const ClearButton = styled(StyledButton)`
  background-color: #ffc107;
  color: #000;
  
  &:hover {
    background-color: #e0a800;
  }
`;

const ActionButton = styled(StyledButton)`
  background-color: #1976d2;
  
  &:hover {
    background-color: #115293;
  }
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '85%',
  maxWidth: '1000px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: '8px'
};

const ModalTitle = styled.h2`
  margin: 0 0 20px 0;
  color: #1976d2;
  font-size: 24px;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 10px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const UserInboxFormCIR = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedConsum, setSelectedConsum] = useState([]);
  const [addSpecIndex, setAddSpecIndex] = useState(1);
  const [openEditModal, setOpenEditModal] = useState(false);
  
  const auth = useAuth();
  const user_current_office = useSelector((state) => state.office.off);
  const alert = useAlert();
  
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  const { storeofficedata } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({
      storeofficedata: data?.entities
    }),
  });

  const [updateConsumableReq] = useUpdateConsumableReqMutation();
  const [updateconsumfinal] = useUpdateconsumfinalMutation();
  const [updateofficeitems] = useUpdateOfficeItemMutation();
  const [addNewConsumTransaction] = useAddNewConsumTransactionMutation();
  const [updateBoPartialConsumable] = useUpdateBoPartialConsumableMutation();

  // Get BO details for modification scenario
  const Bo_id = useMemo(() => {
    const useroffice = officedata && Object.values(officedata || {})?.find(elm => elm.name === user_current_office);
    return useroffice?.Bo;
  }, [officedata, user_current_office]);

  // Determine the mode: 'modify' or 'accept'
  const formMode = useMemo(() => {
    const lastAction = row?.req_data?.[row.req_data.length - 1];
    const lastTag = lastAction?.sent_to_tag;
    
    if (lastTag === 'bo_returnto_user_cir') {
      return 'modify';
    }
    
    return 'accept';
  }, [row]);

  // Load existing consumables when in modification mode
  useEffect(() => {
    if (formMode === 'modify' && row?.consumables_requested?.length > 0) {
      const lastRequest = row.consumables_requested[row.consumables_requested.length - 1];
      const consumablesData = lastRequest.data || [];
      
      // Transform the data to match the UnivConsumSelector format
      const transformedData = consumablesData.map(item => ({
        consumable: item.consumable,
        asset: item.asset,
        assettag: item.assettag,
        partno: item.partno || '',
        quantity: item.quantity
      }));
      
      setSelectedConsum(transformedData);
      setAddSpecIndex(transformedData.length); // Set to length, not length + 1, to avoid extra row
    }
  }, [formMode, row]);

  // Determine the request type and consumables to display (for accept mode)
  const { requestType, consumablesToShow, isPartialIssue, issueSource } = useMemo(() => {
    const lastAction = row?.req_data?.[row.req_data.length - 1];
    const lastTag = lastAction?.sent_to_tag;
    
    let type = 'unknown';
    let consumables = [];
    let partial = false;
    let source = 'bo';
    
    // Get original requested consumables (always available)
    const originalRequestedConsumables = row?.consumables_requested?.[row.consumables_requested.length - 1]?.data || [];
    
    // Check if this is a BO direct issue (approved by UnitHead)
    if (lastTag === 'uhapprv_fullissue_close') {
      type = 'bo_full_issue';
      partial = false;
      source = 'bo';
      // Get BO issued consumables
      if (row?.bo_consum_issue?.length > 0) {
        const boIssuedConsumables = row.bo_consum_issue[row.bo_consum_issue.length - 1].data || [];
        // Use BO issued consumables directly as they contain the actual part numbers being issued
        // This handles cases where BO splits one requested item into multiple part numbers
        consumables = boIssuedConsumables.map(issuedItem => {
          // originalRequestedQty = allocated portion when BO split (e.g., 6 out of 8 for Black toner)
          // quantity = what BO actually issued (e.g., 6 for full issue, or less for partial)
          const allocatedQty = issuedItem.originalRequestedQty ? parseInt(issuedItem.originalRequestedQty, 10) : parseInt(issuedItem.quantity, 10);
          const issuedQty = parseInt(issuedItem.quantity, 10);
          
          return {
            consumable: issuedItem.consumable,
            partno: issuedItem.partno,
            originalQuantity: allocatedQty, // Total Items Requested (allocated per split row by BO, e.g., 6, 1, 1)
            boIssuedQuantity: issuedQty, // Issued at BO (what was actually issued)
            storeIssuedQuantity: 0, // Store not involved in BO full issue
            issuedQuantity: issuedQty, // For full issue, BO issued = total issued
            quantity: allocatedQty, // For calculations
            modReqQuantity: issuedItem.modReqQuantity, // Modified requested quantity for this part
            asset: issuedItem.asset,
            assettag: issuedItem.assettag
          };
        });
      } else {
        consumables = originalRequestedConsumables;
      }
    } else if (lastTag === 'uhapprv_partissue_close') {
      type = 'bo_partial_issue';
      partial = true;
      source = 'bo';
      const boIssuedConsumables = row?.bo_consum_issue?.[row.bo_consum_issue.length - 1]?.data || [];
      // Use BO issued consumables directly as they contain the actual part numbers being issued
      // This handles cases where BO splits one requested item into multiple part numbers
      consumables = boIssuedConsumables.map(issuedItem => {
        // originalRequestedQty = allocated portion when BO split (e.g., 6 out of 8 for Black toner)
        // quantity = what BO actually issued (e.g., 1 for partial of the 6 allocated)
        const allocatedQty = issuedItem.originalRequestedQty ? parseInt(issuedItem.originalRequestedQty, 10) : parseInt(issuedItem.quantity, 10);
        const issuedQty = parseInt(issuedItem.quantity, 10);
        
        return {
          consumable: issuedItem.consumable,
          partno: issuedItem.partno,
          originalQuantity: allocatedQty, // Total Items Requested (allocated per split row by BO, e.g., 6, 1, 1)
          boIssuedQuantity: issuedQty, // Issued at BO (what was actually issued, might be partial)
          storeIssuedQuantity: 0, // Store not involved in BO partial close
          issuedQuantity: issuedQty, // For partial close, BO issued = total issued
          quantity: allocatedQty, // For calculations
          modReqQuantity: issuedItem.modReqQuantity, // Modified requested quantity for this part
          asset: issuedItem.asset,
          assettag: issuedItem.assettag
        };
      });
    } else if (lastTag === 'uhapprv_partissue_fwd') {
      type = 'bo_partial_forward';
      partial = true;
      source = 'bo';
      const boIssuedConsumables = row?.bo_consum_issue?.[row.bo_consum_issue.length - 1]?.data || [];
      // Use BO issued consumables directly as they contain the actual part numbers being issued
      // This handles cases where BO splits one requested item into multiple part numbers
      consumables = boIssuedConsumables.map(issuedItem => {
        // originalRequestedQty = allocated portion when BO split (e.g., 6 out of 8 for Black toner)
        // quantity = what BO actually issued (e.g., 1 for partial of the 6 allocated)
        const allocatedQty = issuedItem.originalRequestedQty ? parseInt(issuedItem.originalRequestedQty, 10) : parseInt(issuedItem.quantity, 10);
        const issuedQty = parseInt(issuedItem.quantity, 10);
        
        return {
          consumable: issuedItem.consumable,
          partno: issuedItem.partno,
          originalQuantity: allocatedQty, // Total Items Requested (allocated per split row by BO, e.g., 6, 1, 1)
          boIssuedQuantity: issuedQty, // Issued at BO (what was actually issued, might be partial)
          storeIssuedQuantity: 0, // Store hasn't issued yet
          issuedQuantity: issuedQty, // For partial forward, BO issued = what user receives now
          quantity: allocatedQty, // For calculations
          modReqQuantity: issuedItem.modReqQuantity, // Modified requested quantity for this part
          asset: issuedItem.asset,
          assettag: issuedItem.assettag
        };
      });
    } else if (lastTag === 'storel2_cir_forward_user') {
      type = 'store_issue';
      source = 'store';
      const storeIssuedConsumables = row?.store_consum_issue?.[row.store_consum_issue.length - 1]?.data || [];
      
      // First, calculate quantities for each unique consumable+asset+assettag
      const quantityMap = new Map();
      
      for (const originalItem of originalRequestedConsumables) {
        const key = `${originalItem.consumable}|${originalItem.asset}|${originalItem.assettag?.label || 'NA'}`;
        const originalQty = parseInt(originalItem.quantity, 10);
        
        // Calculate BO issued quantity for this item
        let boIssuedQty = 0;
        if (row?.bo_consum_issue && row.bo_consum_issue.length > 0) {
          const latestBoIssue = row.bo_consum_issue[row.bo_consum_issue.length - 1];
          if (latestBoIssue?.data) {
            const boIssuedItems = latestBoIssue.data.filter(boItem => 
              boItem.consumable === originalItem.consumable && 
              boItem.asset === originalItem.asset &&
              (boItem.assettag?.label === originalItem.assettag?.label)
            );
            boIssuedQty = boIssuedItems.reduce((sum, boItem) => 
              sum + parseInt(boItem.quantity, 10), 0
            );
          }
        }
        
        // Remaining quantity = Original - BO issued
        const remainingQty = originalQty - boIssuedQty;
        quantityMap.set(key, {
          originalQty,
          boIssuedQty,
          remainingQty
        });
      }
      
      // Transform store issued consumables to include all three quantity values
      consumables = storeIssuedConsumables.map(issuedItem => {
        const key = `${issuedItem.consumable}|${issuedItem.asset}|${issuedItem.assettag?.label || 'NA'}`;
        const qtyInfo = quantityMap.get(key) || {
          originalQty: parseInt(issuedItem.quantity, 10),
          boIssuedQty: 0,
          remainingQty: parseInt(issuedItem.quantity, 10)
        };
        
        return {
          consumable: issuedItem.consumable,
          partno: issuedItem.partno,
          originalQuantity: issuedItem.originalQuantity || qtyInfo.remainingQty, // Total Items Requested (Store's allocated portion)
          boIssuedQuantity: qtyInfo.boIssuedQty, // Issued at BO level
          storeIssuedQuantity: issuedItem.selectedQuantity || issuedItem.quantity, // Issued at Store level
          issuedQuantity: issuedItem.selectedQuantity || issuedItem.quantity, // Issued at Store level
          quantity: qtyInfo.remainingQty, // For internal calculations
          modReqQuantity: issuedItem.modReqQuantity, // Modified requested quantity for this part
          asset: issuedItem.asset,
          assettag: issuedItem.assettag
        };
      });
      
      // Calculate if this is a partial issue
      // Group by consumable+asset+assettag and compare total issued vs remaining
      const groupedIssued = new Map();
      
      for (const item of consumables) {
        const key = `${item.consumable}|${item.asset}|${item.assettag?.label || 'NA'}`;
        const currentIssued = groupedIssued.get(key) || 0;
        groupedIssued.set(key, currentIssued + parseInt(item.issuedQuantity, 10));
      }
      
      // Check if any group has less issued than remaining
      partial = false;
      for (const [key, totalIssued] of groupedIssued) {
        const qtyInfo = quantityMap.get(key);
        if (qtyInfo && totalIssued < qtyInfo.remainingQty) {
          partial = true;
          break;
        }
      }
    }
    
    // Debug logging
    console.log('UserInbox Data Debug:', {
      lastTag,
      requestType: type,
      originalRequestedConsumables,
      consumablesToShow: consumables,
      isPartialIssue: partial,
      issueSource: source,
      sampleData: consumables.slice(0, 2).map(item => ({
        consumable: item.consumable,
        partno: item.partno,
        quantity: item.quantity,
        issuedQuantity: item.issuedQuantity
      }))
    });

    return {
      requestType: type,
      consumablesToShow: consumables,
      isPartialIssue: partial,
      issueSource: source
    };
  }, [row]);

  // Generate summary data from consumables
  const generateSummary = useCallback((consumables) => {
    if (!consumables || consumables.length === 0) return [];

    const summary = consumables.reduce((acc, item) => {
      const key = item.consumable;
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += parseInt(item.issuedQuantity || item.quantity, 10);
      return acc;
    }, {});

    return Object.entries(summary).map(([name, count]) => ({ name, count }));
  }, []);

  // Validation function for consumables
  const hasValidQuantity = useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
    
    return data.every(item => {
      if (!item || !item.quantity) return false;
      const quantity = parseInt(item.quantity, 10);
      return !isNaN(quantity) && quantity > 0;
    });
  }, []);

  // Normalize consumable for comparison (similar to BoInboxFormBTS)
  const normalizeForComparison = useCallback((item) => ({
    consumable: item.consumable,
    quantity: parseInt(item.quantity, 10),
    asset: item.asset || 'Not Applicable',
    assettagLabel: item.assettag?.label || 'Not Applicable'
  }), []);

  // Compare consumable arrays
  const areConsumablesEqual = useCallback((currentItems, previousItems) => {
    if (!Array.isArray(currentItems) || !Array.isArray(previousItems)) {
      return false;
    }
    
    // Quick check for array length difference
    if (currentItems.length !== previousItems.length) {
      return false;
    }
    
    // Normalize both arrays for comparison
    const normalizedCurrent = currentItems.map(normalizeForComparison);
    const normalizedPrevious = previousItems.map(normalizeForComparison);
    
    // Check if each item in current exists in previous with same properties
    return normalizedCurrent.every(currentItem => 
      normalizedPrevious.some(prevItem => 
        prevItem.consumable === currentItem.consumable &&
        prevItem.quantity === currentItem.quantity &&
        prevItem.asset === currentItem.asset &&
        prevItem.assettagLabel === currentItem.assettagLabel
      )
    );
  }, [normalizeForComparison]);

  // Modal handlers
  const handleOpenEditModal = useCallback(() => {
    setOpenEditModal(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setOpenEditModal(false);
  }, []);

  const handleConfirmSelection = useCallback(() => {
    setOpenEditModal(false);
  }, []);

  const handleClearAllSelections = useCallback(() => {
    setSelectedConsum([]);
    setAddSpecIndex(1);
  }, []);

  // Handle modification and resubmission
  const handleModifyAndResubmit = useCallback(async () => {
    
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark explaining your modifications.");
      return;
    }

    if (!hasValidQuantity(selectedConsum)) {
      alert.showWarning("Please enter valid quantities for all items");
      return;
    }

    if (selectedConsum.length === 0) {
      alert.showWarning("Please select at least one consumable");
      return;
    }

    try {
      setIsLoading(true);

      // Check if the consumables selection has changed 
      let originalConsumables = [];
      if (row?.consumables_requested && row.consumables_requested.length > 0) {
        const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
        originalConsumables = latestRequest.data || [];
      }
      
      const selectionUnchanged = areConsumablesEqual(selectedConsum, originalConsumables);

      // Create the sent_to data
      const senttoData = {
        sent_to_tag: "user_modified_resubmit",
        Text: remark,
        action: selectionUnchanged 
          ? "User resubmitted request to BO (no consumable changes)" 
          : "User modified request and resubmitted to BO",
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: Bo_id, 
          name: getSingleuser?.[Bo_id]?.name, 
          office: user_current_office, 
          designation: getSingleuser?.[Bo_id]?.designation?.[user_current_office] 
        },
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[Bo_id])}-${user_current_office}-${getSingleuser?.[Bo_id]?.designation?.[user_current_office] || 'Unknown'}`,
        time_stamp: new Date().toISOString(),
        curr_status: "Pending"
      };

      // If consumables were changed, update the request with the new selection
      if (!selectionUnchanged || !row.consumables_requested) {
        senttoData.consumables_requested = [{
          time_stamp: new Date(),
          actionNo: (row?.consumables_requested?.length || 0) + 1,
          data: selectedConsum.map(item => ({
            consumable: item.consumable,
            asset: item?.asset ? item.asset.trim() : "Not Applicable",
            assettag: item?.assettag ? item.assettag : {label: "Not Applicable", value: "Not Applicable"},
            partno: item.partno || '',
            quantity: item.quantity
          }))
        }];
      }

      const body = { 
        id: row?._id, 
        senttoData
      };

      await updateConsumableReq(body).unwrap();
      
      let message;
      if (selectionUnchanged) {
        message = "Request resubmitted to BO without any changes to consumables. Only remarks were updated.";
      } else {
        message = "Request resubmitted to BO with updated consumables.";
      }
      
      alert.showSuccess(message, "Request Resubmitted", handleBack);
    } catch (error) {
      console.error("Error resubmitting request:", error);
      alert.showError(`There was an error resubmitting the request: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [
    remark, 
    selectedConsum, 
    hasValidQuantity, 
    row, 
    auth, 
    user_current_office, 
    Bo_id,
    getSingleuser,
    areConsumablesEqual,
    updateConsumableReq, 
    handleBack,
    alert
  ]);

  // Handle accepting consumables
  const handleAcceptConsumables = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark before accepting the consumables.");
      return;
    }

    try {
      setIsLoading(true);

      // Determine from_desig based on issueSource
      let from_desig = "";
      
      if (issueSource === 'store') {
        // For store issue, get L2ReqApprover_C designation
        const storeOffice = Object.values(storeofficedata || {}).find(store => 
          store.storeoffice === row?.req_store
        );
        const l2ReqApproverId = storeOffice?.roleUsers?.L2ReqApprover_C;
        from_desig = l2ReqApproverId ? 
          getSingleuser?.[l2ReqApproverId]?.designation?.[row?.req_store] || 'L2ReqApprover_C' :
          'L2ReqApprover_C';
      } else {
        // For BO issue, get BO designation from the office from which user requested
        const userOffice = Object.values(officedata || {}).find(office => 
          office.name === user_current_office
        );
        const boId = userOffice?.Bo;
        from_desig = boId ? 
          getSingleuser?.[boId]?.designation?.[user_current_office] || 'Bo' :
          'Bo';
      }

      // Create transaction object template
      // Get from_userid email by looking up the user ID
      const fromUserId = issueSource === 'store' ? 
        row?.req_data?.find(d => d.sent_to_tag?.includes('storel2'))?.sent_by?.id || auth?.id :
        row?.req_data?.find(d => d.sent_to_tag?.includes('boreq_'))?.sent_by?.id || auth?.id;
      const fromUserEmail = getSingleuser?.[fromUserId]?.email || auth?.email;
      
      // Determine transaction type based on issue scenario
      let transac_type;
      if (issueSource === 'store') {
        // Store to User scenarios
        transac_type = isPartialIssue ? "StoreToUserP" : "StoreToUser";
      } else {
        // BO to User scenarios
        if (requestType === 'bo_partial_forward') {
          transac_type = "BoToUserPF"; // Partial with Forward to store
        } else if (requestType === 'bo_partial_issue') {
          transac_type = "BoToUserPC"; // Partial Close (no forward)
        } else {
          transac_type = "BoToUser"; // Full issue
        }
      }
      
      const transactionTemplate = {
        transac_id: `UserAccept-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`,
        req_id: row?._id || "",
        consumable: "",
        partnumber: "",
        quantity: "",
        asset_id: "",
        transac_type: transac_type,
        from_userid: fromUserEmail,
        from_office: issueSource === 'store' ? row?.req_store : user_current_office,
        from_desig: from_desig,
        from_store: row?.req_store,
        to_userid: auth?.email,
        to_office: user_current_office,
        to_desig: auth?.designation?.[user_current_office],
        to_store: row?.req_store,
        time_stamp: new Date().toISOString()
      };

      // Determine the appropriate sent_to_tag based on request type
      let sent_to_tag;
      let action;
      let sent_to_user = auth?.id;
      let sent_to_office = user_current_office;
      let sent_to_designation = auth?.designation?.[user_current_office];
      let curr_status = "Closed";
      
      // Special case: bo_partial_forward - user accepts partial, then forward to store
      if (requestType === 'bo_partial_forward') {
        // Get store L1 manager
        const storeOffice = Object.values(storeofficedata || {}).find(store => 
          store.storeoffice === row?.req_store
        );
        const storeL1Id = storeOffice?.roleUsers?.L1ReqApprover_C;
        
        if (!storeL1Id) {
          alert.showError("Store L1 manager not found. Cannot forward to store.");
          throw new Error("Store L1 manager not found");
        }
        
        sent_to_tag = "ureq_paraccept_fwd_store";
        action = "Partial consumables accepted by user, forwarding remaining to store";
        sent_to_user = storeL1Id;
        sent_to_office = row?.req_store;
        sent_to_designation = getSingleuser?.[storeL1Id]?.designation?.[row?.req_store];
        curr_status = "Pending"; // Keep as pending since going to store
      } else if (isPartialIssue) {
        sent_to_tag = "ureq_paraccept_close";
        action = `Partial consumables accepted and request closed (${issueSource === 'store' ? 'Store Issue' : 'BO Issue'})`;
      } else {
        sent_to_tag = issueSource === 'store' ? "ureq_storeassetsaccpt_close" : "ureq_allasstbolvl_close";
        action = `All consumables accepted and request closed (${issueSource === 'store' ? 'Store Issue' : 'BO Issue'})`;
      }

      const senttoData = {
        sent_to_tag,
        Text: remark,
        action,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: sent_to_user, 
          name: getSingleuser?.[sent_to_user]?.name, 
          office: sent_to_office, 
          designation: sent_to_designation
        },
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[sent_to_user])}-${sent_to_office}-${sent_to_designation || 'Unknown'}`,
        time_stamp: new Date().toISOString(),
        curr_status: curr_status
      };

      // If this is a store issue, update the store consumable issue status from draft to final
      if (issueSource === 'store' && row?.store_consum_issue?.length > 0) {
        const latestStoreIssue = row.store_consum_issue[row.store_consum_issue.length - 1];
        // Update the status to final
        senttoData.store_consum_issue_update = {
          index: row.store_consum_issue.length - 1,
          status: 'final',
          accept_time: new Date().toISOString()
        };
      }

      const body = { id: row?._id, senttoData };

      // Update request status
      await updateConsumableReq(body).unwrap();

      // If this is a BO issue, update the BO consumable issue status from draft to final
      if (issueSource === 'bo') {
        try {
          await updateBoPartialConsumable({
            id: row?._id,
            accept_time: new Date().toISOString(),
            status: 'final'
          }).unwrap();
        } catch (error) {
          console.error("Error updating BO partial consumable status:", error);
          // Continue even if this fails - it's not critical
        }
      }

      // Filter out items with 0 issued quantity before processing APIs
      // For Store issues: filter items with storeIssuedQuantity = 0
      // For BO issues: include all items (BO quantities are already final)
      const itemsToProcess = issueSource === 'store' 
        ? consumablesToShow.filter(item => parseInt(item.storeIssuedQuantity || 0, 10) > 0)
        : consumablesToShow;
      
      console.log(`Processing ${itemsToProcess.length} items out of ${consumablesToShow.length} displayed items`);

      // Process each consumable item sequentially to avoid race conditions
      for (const item of itemsToProcess) {
        const quantity = parseInt(item.issuedQuantity || item.quantity, 10);
        
        // Create transaction record for each item
        const itemTransaction = {
          ...transactionTemplate,
          transac_id: `UserAccept-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`,
          consumable: item.consumable,
          partnumber: item.partno,
          quantity: quantity,
          asset_id: item.assettag && typeof item.assettag.value === 'object' ? item.assettag.value.oem_tag : ""
        };

        try {
          // Update inventory if coming from store
          if (issueSource === 'store') {
            await updateconsumfinal({
              consumable: item.consumable,
              partno: item.partno,
              store: row.req_store,
              isInTransit: -quantity // Decrease in-transit
            }).unwrap();
          }

          // Update office items (reduce BO's office inventory) - only when BO issues to user
          if (issueSource === 'bo') {
            await updateofficeitems({
              office_id: Object.values(officedata).find(x => x.name === user_current_office)?.id,
              office_name: user_current_office,
              consumable_details: [{
                partno: item.partno,
                consumable: item.consumable,
                quantity: -quantity // Negative quantity to reduce BO office inventory
              }]
            });
          }

          // Add transaction record
          await addNewConsumTransaction(itemTransaction);
        } catch (error) {
          console.error(`Error processing item ${item.consumable}:`, error);
          throw new Error(`Failed to process ${item.consumable}: ${error.message}`);
        }
      }

      // Success message based on request type
      if (requestType === 'bo_partial_forward') {
        alert.showSuccess('Partial consumables accepted successfully. Request has been forwarded to store for remaining items.', 'Success', handleBack);
      } else {
        alert.showSuccess(`${isPartialIssue ? 'Partial' : 'All'} consumables accepted successfully. Request has been closed.`, 'Success', handleBack);
      }
    } catch (error) {
      console.error("Error accepting consumables:", error);
      alert.showError(`There was an error accepting the consumables: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [
    remark, 
    row, 
    consumablesToShow, 
    isPartialIssue, 
    issueSource, 
    requestType,
    auth, 
    user_current_office, 
    getSingleuser, 
    officedata,
    storeofficedata,
    updateConsumableReq, 
    updateconsumfinal, 
    updateofficeitems, 
    addNewConsumTransaction, 
    handleBack,
    alert
  ]);

  // Handle rejecting consumables
  const handleRejectConsumables = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a reason for rejecting the consumables.");
      return;
    }

    try {
      setIsLoading(true);

      const senttoData = {
        sent_to_tag: "ureq_consumables_rejected",
        Text: remark,
        action: "Consumables rejected by user. Request terminated.",
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[auth?.id])}-${user_current_office}-${auth?.designation?.[user_current_office] || 'Unknown'}`,
        time_stamp: new Date(),
        curr_status: "Rejected"
      };

      const body = { id: row?._id, senttoData };

      await updateConsumableReq(body).unwrap();

      // If from store, update inventory to move items back from in-transit
      if (issueSource === 'store') {
        await Promise.all(consumablesToShow.map(async (item) => {
          const quantity = parseInt(item.issuedQuantity || item.quantity, 10);
          await updateconsumfinal({
            consumable: item.consumable,
            partno: item.partno,
            store: row.req_store,
            isInTransit: -quantity, // Decrease in-transit
            balanceQuantity: quantity // Return to balance
          }).unwrap();
        }));
      }

      alert.showSuccess("Consumables rejected successfully. Request has been terminated.", "Request Rejected", handleBack);
    } catch (error) {
      console.error("Error rejecting consumables:", error);
      alert.showError("There was an error rejecting the consumables. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [remark, row, consumablesToShow, issueSource, auth, user_current_office, getSingleuser, updateConsumableReq, updateconsumfinal, handleBack, alert]);

  // Get notification message based on request type
  const getNotificationMessage = () => {
    switch (requestType) {
      case 'bo_full_issue':
        return {
          type: 'full',
          text: 'All requested consumables have been issued by the Branch Office and approved by Unit Head. Please review and accept or reject the consumables.'
        };
      case 'bo_partial_issue':
        return {
          type: 'partial',
          text: 'Partial consumables have been issued by the Branch Office and approved by Unit Head. Some items or quantities may be different from your original request.'
        };
      case 'bo_partial_forward':
        return {
          type: 'partial',
          text: 'Partial consumables issued by BO for your acceptance. Balance request has been forwarded to store.'
        };
      case 'store_issue':
        return {
          type: 'store',
          text: `${isPartialIssue ? 'Partial' : 'All'} consumables have been issued by the Store for your acceptance.`
        };
      default:
        return {
          type: 'info',
          text: 'Consumables are ready for your acceptance. Please review the details below.'
        };
    }
  };

  const notification = getNotificationMessage();

  return (
    <>
      {/* Remarks field */}
      <RemarkContainer>
        <TextField
          fullWidth
          label="Remarks"
          variant="outlined"
          multiline
          rows={4}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Add your remarks here..."
        />
      </RemarkContainer>

      {/* For modification mode */}
      {formMode === 'modify' && selectedConsum?.length > 0 ? (
        <ConsumablesContainer>
          <SelectedAssetsTitleContainer>
            <SelectedAssetsTitle>Consumables Requested for Issue</SelectedAssetsTitle>
            <ActionButtonsContainer>
              <Tooltip title="Edit selections">
                <IconButton color="primary" onClick={handleOpenEditModal}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </ActionButtonsContainer>
          </SelectedAssetsTitleContainer>

          {/* Notification about BO's return reason */}
          <NotificationMessage type="partial">
            <strong>BO has returned this request for modification.</strong>
          </NotificationMessage>

          {/* Summary section */}
          <SummaryContainer>
            <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
              Total Items: {selectedConsum.length}
            </span>
            {generateSummary(selectedConsum).map((item, index) => (
              <SummaryItem key={index}>
                <SummaryItemName>{item.name}:</SummaryItemName>
                <SummaryItemCount>{item.count}</SummaryItemCount>
              </SummaryItem>
            ))}
          </SummaryContainer>

          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Sl.No.</TableCell>
                  <TableCell>Consumable</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Part No.</TableCell>
                  <TableCell>Requested Qty</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {selectedConsum.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.consumable}</TableCell>
                    <TableCell>{!item.asset || item.asset.trim().toLowerCase() === 'not applicable' ? 'N/A' : item.asset}</TableCell>
                    <TableCell>{item.assettag?.label === 'Not Applicable' || !item.assettag?.label ? 'N/A' : item.assettag?.label}</TableCell>
                    <TableCell>{item.partno || '-'}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </ConsumablesContainer>
      ) : formMode === 'accept' ? (
        <>
          {/* Display notification */}
          <NotificationMessage type={notification.type}>
            {notification.text}
          </NotificationMessage>

          {/* Display consumables */}
          {consumablesToShow?.length > 0 && (
        <ConsumablesContainer>
          <ConsumablesTitle>Consumables Ready for Acceptance</ConsumablesTitle>
          
          {/* Summary section */}
          <SummaryContainer>
            <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
              Total Items: {consumablesToShow.length}
            </span>
            {generateSummary(consumablesToShow).map((item, index) => (
              <SummaryItem key={index}>
                <SummaryItemName>{item.name}:</SummaryItemName>
                <SummaryItemCount>{item.count}</SummaryItemCount>
              </SummaryItem>
            ))}
          </SummaryContainer>

          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Sl.No.</TableCell>
                  <TableCell>Consumable</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Part No.</TableCell>
                  <TableCell>Total Items Requested</TableCell>
                  <TableCell>Issued at BO</TableCell>
                  <TableCell>Issued at Store</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {consumablesToShow.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.consumable}</TableCell>
                    <TableCell>{!item.asset || item.asset.trim().toLowerCase() === 'not applicable' ? 'N/A' : item.asset}</TableCell>
                    <TableCell>{item.assettag?.label === 'Not Applicable' || !item.assettag?.label ? 'N/A' : item.assettag?.label}</TableCell>
                    <TableCell>{item.partno}</TableCell>
                    <TableCell>{item.modReqQuantity}</TableCell>
                    <TableCell>{item.boIssuedQuantity || 0}</TableCell>
                    <TableCell style={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {item.storeIssuedQuantity || 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
          </ConsumablesContainer>
          )}

          {/* Action buttons for acceptance mode */}
          <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <AcceptButton 
              onClick={handleAcceptConsumables} 
              disabled={isLoading || consumablesToShow.length === 0}
            >
              {isLoading 
                ? 'Processing...' 
                : requestType === 'bo_partial_forward' 
                  ? 'Accept Partial & Forward Remaining to Store'
                  : 'Accept Consumables & Close Request'
              }
            </AcceptButton>
          </div>
        </>
      ) : null}

      {/* Resubmit button for modification mode */}
      {formMode === 'modify' && (
        <ReSubmitButton 
          onClick={handleModifyAndResubmit} 
          disabled={isLoading || selectedConsum.length === 0}
        >
          {isLoading ? 'Processing...' : 'Resubmit to BO'}
        </ReSubmitButton>
      )}
      
      {/* Modal for Edit Issue */}
      <Modal
        open={openEditModal}
        onClose={handleCloseEditModal}
        aria-labelledby="edit-modal-title"
      >
        <Box sx={ModalStyle}>
          <ModalTitle id="edit-modal-title">Modify Consumables Request</ModalTitle>  

          <UnivConsumSelector
            addSpecIndex={addSpecIndex}
            setAddSpecIndex={setAddSpecIndex}
            selectedConsum={selectedConsum}
            setSelectedConsum={setSelectedConsum}
            storeSelectedValue={row?.req_store}
            hidePartNumber={true}
          />        
          
          <ModalFooter>
            <CancelButton onClick={handleCloseEditModal}>
              Cancel
            </CancelButton>
            <ClearButton onClick={handleClearAllSelections}>
              Clear All
            </ClearButton>
            <ActionButton onClick={handleConfirmSelection}>
              Confirm Selection
            </ActionButton>
          </ModalFooter>
        </Box>
      </Modal>

    </>
  );
};

export default UserInboxFormCIR;
